import { FC, FormEvent, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box, Typography, TextField, Select, MenuItem } from '@mui/material';

import { CampaignPPC, CampaignPartner, CampaignStatus } from '@libs/types';
import { CampaignUpdateDto } from '@libs/dto';
import { useForm, useDrawer } from '@carcareers/contexts';
import { CurrencyInput, DateRangeInput } from '@carcareers/components';
import { useCampaign } from '@carcareers/hooks';

interface CampaignUpdateFormProps {
  onSuccess?: () => void;
  disablePartner?: boolean;
  disablePPC?: boolean;
  disableStatus?: boolean;
  disableBudget?: boolean;
}

export const CampaignUpdateForm: FC<CampaignUpdateFormProps> = ({
  onSuccess = () => undefined,
  disablePartner = false,
  disablePPC = false,
  disableStatus = false,
  disableBudget = false,
}) => {
  const navigate = useNavigate();
  const { setOpen } = useDrawer();
  const { data, setData, setErrors } = useForm<CampaignUpdateDto>();
  const { updateCampaign } = useCampaign();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const transformedData = {
      ...data,
      ppc: `${data?.ppc?.toUpperCase().substring(0, 1)}${data?.ppc?.toLowerCase().substring(1)}` as CampaignPPC,
      status: `${data?.status?.toUpperCase().substring(0, 1)}${data?.status?.toLowerCase().substring(1)}` as CampaignStatus,
      budget: Number(data.budget),
    };

    const response = await updateCampaign(transformedData, { onSuccess });

    if (response?.data) {
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  const ppcOptions = useMemo(
    () =>
      Object.entries(CampaignPPC).map(([key, value]) => ({
        label: value.toString(),
        value: key,
      })),
    [],
  );

  const statusOptions = useMemo(
    () =>
      Object.entries(CampaignStatus).map(([key, value]) => ({
        label: value,
        value: key,
      })),
    [],
  );

  const inputSx = useMemo(
    () => ({
      input: {
        sx: {
          borderRadius: 3,
        },
      },
    }),
    [],
  );

  return (
    <>
      <TextField
        value={data?.partner || ''}
        onChange={(e) => setData('partner', e.target.value)}
        name="partner"
        label="Partner"
        disabled={disablePartner}
        slotProps={inputSx}
      />
      <Select
        value={data?.status?.toUpperCase() || ''}
        onChange={(e) => setData('status', e.target.value)}
        name="status"
        disabled={disableStatus}
        sx={{ borderRadius: 3 }}
      >
        {statusOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <Select
        value={data?.ppc?.toUpperCase() || ''}
        onChange={(e) => setData('ppc', e.target.value)}
        name="ppc"
        disabled={disablePPC}
        sx={{ borderRadius: 3 }}
      >
        {ppcOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <CurrencyInput value={data?.budget || 0} onChange={(e) => setData('budget', e || 0)} disabled={disableBudget} />
      <Typography variant="h6">Campaign Duration</Typography>
      <DateRangeInput nameStart="startDate" nameEnd="endDate" />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSubmit}
          sx={{ borderRadius: 2 }}
        >
          Update Campaign
        </Button>
      </Box>
    </>
  );
};

export default CampaignUpdateForm;
